"use client";

import { cn } from "../../lib";
import { SQ_PRIMARY_CTA_GRADIENT_CLASS } from "../../lib/sq-color-runtime-exports";
import { GlassBorder, GlassBorderPanel } from "./glass-border";

/** Match pricing plan-header / shop mode glass band thickness. */
export const GLASS_MODE_TAB_GLASS_BAND_PX = 3;

/** Horizontal inset so adjacent glass tabs don't touch. */
export const GLASS_MODE_TAB_COL_CLASS =
	"min-h-0 min-w-0 items-stretch self-stretch px-1.5 py-0";

/** Visible tab body height before the amber bar (inactive baseline). */
export const GLASS_MODE_TAB_CLIP_HEIGHT_CLASS = {
	default: "h-10",
	compact: "h-8",
} as const;

/** Fixed amber bar — always attached to the tab bottom. */
export const GLASS_MODE_TAB_AMBER_BAR_CLASS = {
	default: "h-2.5",
	compact: "h-2",
} as const;

/** Row-level bottom clip with horizontal bleed (tablist / plan-header row). */
export const GLASS_MODE_TAB_ROW_CLIP_CLASS =
	"[clip-path:inset(-100px_-6px_0_-6px)]";

/** Tablist / header row — fixed inactive baseline height + bottom clip. */
export function glassModeTabRowClassName(compact = false): string {
	return cn(
		GLASS_MODE_TAB_ROW_CLIP_CLASS,
		compact
			? GLASS_MODE_TAB_CLIP_HEIGHT_CLASS.compact
			: GLASS_MODE_TAB_CLIP_HEIGHT_CLASS.default,
	);
}

/** Inactive offset — pushes the bar below the clip edge. Active is `translate-y-0`. */
export const GLASS_MODE_TAB_INACTIVE_OFFSET_CLASS = {
	default: "translate-y-2.5",
	compact: "translate-y-2",
} as const;

/**
 * Tab body (icon + label) — muted grayscale when idle; full colour on hover /
 * active. Mirrors magic-cards image treatment.
 */
export const GLASS_MODE_TAB_CONTENT_INTERACTIVE =
	"opacity-50 grayscale transition-[filter,opacity] duration-300 ease-out group-hover:opacity-100 group-hover:grayscale-0 group-data-[active]:opacity-100 group-data-[active]:grayscale-0 group-focus-visible:opacity-100 group-focus-visible:grayscale-0";

/** Frozen treatment for disabled tabs — no hover / active colour lift. */
export const GLASS_MODE_TAB_CONTENT_DISABLED =
	"opacity-50 grayscale transition-none";

export interface GlassModeTabChromeProps {
	active: boolean;
	disabled?: boolean;
	compact?: boolean;
	children: React.ReactNode;
	className?: string;
	panelClassName?: string;
	panelDataAttributes?: Record<string, string | undefined>;
}

/**
 * Shared glass tab plate — shop BUY/SELL/CASH, inventory categories, entity
 * editor tabs, pricing plan headers. The SQ amber bar is always attached to the
 * tab bottom; inactive tabs clip it and active tabs slide the whole plate up to
 * reveal it.
 */
export function GlassModeTabChrome({
	active,
	disabled = false,
	compact = false,
	children,
	className,
	panelClassName,
	panelDataAttributes,
}: GlassModeTabChromeProps) {
	const size = compact ? "compact" : "default";
	const clipHeightClass = GLASS_MODE_TAB_CLIP_HEIGHT_CLASS[size];
	const amberBarClass = GLASS_MODE_TAB_AMBER_BAR_CLASS[size];
	const inactiveOffsetClass = GLASS_MODE_TAB_INACTIVE_OFFSET_CLASS[size];

	return (
		<div className={cn("relative w-full overflow-visible", clipHeightClass)}>
			<GlassBorder
				variant="outer"
				tone="system"
				borderWidth={GLASS_MODE_TAB_GLASS_BAND_PX}
				radius="2xl"
				largeBlur
				edgeBorderWidths={{
					top: GLASS_MODE_TAB_GLASS_BAND_PX,
					right: GLASS_MODE_TAB_GLASS_BAND_PX,
					bottom: 0,
					left: GLASS_MODE_TAB_GLASS_BAND_PX,
				}}
				cornerRadii={{
					topLeft: "2xl",
					topRight: "2xl",
					bottomLeft: "none",
					bottomRight: "none",
				}}
				className={cn(
					"absolute inset-x-0 bottom-0 z-0 w-full min-w-0",
					"transition-transform duration-300 ease-out will-change-transform",
					active ? "translate-y-0" : inactiveOffsetClass,
					disabled && "opacity-45",
					className,
				)}
				wrapperClassName="flex w-full min-w-0 flex-col overflow-visible"
				contentClassName="flex w-full flex-col border-transparent"
			>
				<GlassBorderPanel
					className={cn(
						"flex w-full flex-col overflow-hidden px-0",
						"transition-colors duration-300 ease-out",
						active
							? "bg-white dark:bg-zinc-950"
							: cn(
									"bg-zinc-100/95 dark:bg-zinc-900/75",
									!disabled &&
										"hover:bg-zinc-50 dark:hover:bg-zinc-900",
								),
						panelClassName,
					)}
					style={{
						marginTop: -GLASS_MODE_TAB_GLASS_BAND_PX,
						marginLeft: -GLASS_MODE_TAB_GLASS_BAND_PX,
						marginRight: -GLASS_MODE_TAB_GLASS_BAND_PX,
						width: `calc(100% + ${GLASS_MODE_TAB_GLASS_BAND_PX * 2}px)`,
						borderTopLeftRadius: "var(--glass-border-r)",
						borderTopRightRadius: "var(--glass-border-r)",
						borderBottomLeftRadius: 0,
						borderBottomRightRadius: 0,
					}}
					{...panelDataAttributes}
				>
					<div
						className={cn(
							"flex w-full items-center justify-center gap-1 px-0.5",
							compact ? "min-h-7 py-1" : "min-h-9 py-1.5",
						)}
					>
						{children}
					</div>
					<div
						className={cn(
							"w-full shrink-0 border-t border-zinc-900/20 dark:border-zinc-950/50",
							amberBarClass,
							SQ_PRIMARY_CTA_GRADIENT_CLASS,
						)}
						aria-hidden={!active}
					/>
				</GlassBorderPanel>
			</GlassBorder>
		</div>
	);
}
